/**
 * @file     openHouseTime.ts
 * @readme   ./README.md
 * @purpose  Formats MLS open-house timestamps and day labels in the event/listing timezone, deterministically across server and browser.
 */

import { formatMonthDay, formatWeekdayShort, partsOf } from './dateFormat';

const CLOCK_TIME_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
// Remarks-synthesized open houses are stored as zone-less "YYYY-MM-DDTHH:mm:ss".
// They are Vancouver wall clock; new Date() would read them in the runtime zone.
const NAIVE_LOCAL_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
const DAY_MS = 86_400_000;

/**
 * Calendar days from the site-local "today" to an open-house date
 * ("YYYY-MM-DD"). Both sides are reduced to America/Vancouver calendar dates,
 * so a server in UTC and a browser in any timezone agree on "today".
 * Returns null for a malformed date.
 */
export function daysUntilOpenHouse(date: string, now: Date): number | null {
  const event = partsOf(date);
  const today = partsOf(now);
  if (!event || !today) return null;
  const eventUtc = Date.UTC(event.year, event.month - 1, event.day);
  const todayUtc = Date.UTC(today.year, today.month - 1, today.day);
  return Math.round((eventUtc - todayUtc) / DAY_MS);
}

/**
 * "today", "tomorrow", "Sat" (within the week), or "Sep 15". Month and weekday
 * text comes from fixed tables, never from ICU, so it hydrates byte-identical.
 * Returns null for past dates and malformed input.
 */
export function openHouseDayLabel(date: string, now: Date): string | null {
  const diff = daysUntilOpenHouse(date, now);
  if (diff == null || diff < 0) return null;
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff < 7) return formatWeekdayShort(date);
  return formatMonthDay(date);
}

export type OpenHouseLike = {
  date: string;
  startTime: string | null;
  endTime: string | null;
  timeZone: string | null;
};

/** "Open House on Sat, 1–3pm". Null when the event has passed or the date is malformed. */
export function formatOpenHouseLabel(oh: OpenHouseLike | null | undefined, now: Date): string | null {
  if (!oh?.date) return null;
  const day = openHouseDayLabel(oh.date, now);
  if (!day) return null;
  const start = formatOpenHouseTime(oh.startTime, oh.timeZone);
  const end = formatOpenHouseTime(oh.endTime, oh.timeZone);
  const times = start && end ? `${start}–${end}` : (start ?? end ?? '');
  return times ? `Open House on ${day}, ${times}` : `Open House on ${day}`;
}

const TIME_ZONE_ALIASES: Record<string, string> = {
  'pacific time': 'America/Vancouver',
  'mountain time': 'America/Edmonton',
  'central time': 'America/Winnipeg',
  'eastern time': 'America/Toronto',
  'atlantic time': 'America/Halifax',
  'newfoundland time': 'America/St_Johns',
};

export function resolveOpenHouseTimeZone(
  eventTimeZone: string | null | undefined,
): string {
  const candidate = eventTimeZone?.trim();
  if (!candidate) return 'America/Vancouver';
  const alias = TIME_ZONE_ALIASES[candidate.toLowerCase()];
  if (alias) return alias;

  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: candidate }).format();
    return candidate;
  } catch {
    // Both MLS feeds are currently scoped to British Columbia.
    return 'America/Vancouver';
  }
}

/**
 * Site-time wall clock "YYYY-MM-DDTHH:mm" for an open-house timestamp. A
 * zone-less value is already Vancouver wall clock; an instant is converted.
 * Comparable as a string against {@link siteWallClockNow}.
 */
export function openHouseWallClock(value: string | null | undefined): string | null {
  if (!value) return null;
  const naive = NAIVE_LOCAL_RE.exec(value);
  if (naive) return `${naive[1]}T${naive[2]}:${naive[3]}`;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return siteWallClockNow(instant);
}

/** The current Vancouver wall clock as "YYYY-MM-DDTHH:mm". */
export function siteWallClockNow(now: Date): string | null {
  const p = partsOf(now);
  if (!p) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${p.minute}`;
}

/** Today's Vancouver calendar date, "YYYY-MM-DD". */
export function siteToday(now: Date): string {
  return (siteWallClockNow(now) ?? now.toISOString()).slice(0, 10);
}

/**
 * A zone-less Vancouver wall-clock value as an absolute ISO instant, for
 * writing into a timestamptz column (Postgres would otherwise read it as UTC).
 * Values that already carry a zone pass through unchanged.
 */
export function siteWallClockToIso(value: string | null): string | null {
  if (!value) return value;
  const naive = NAIVE_LOCAL_RE.exec(value);
  if (!naive) return value;
  const [y, m, d] = naive[1].split('-').map(Number);
  const wall = Date.UTC(y, m - 1, d, Number(naive[2]), Number(naive[3]));
  // Two passes settle a daylight-saving edge: the first guess picks the offset.
  let guess = wall - siteOffsetMs(wall);
  guess = wall - siteOffsetMs(guess);
  return new Date(guess).toISOString();
}

function siteOffsetMs(utcMs: number): number {
  const p = partsOf(new Date(utcMs));
  if (!p) return 0;
  const minuteFloor = utcMs - (utcMs % 60_000);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, Number(p.minute)) - minuteFloor;
}

/** Formats either a local HH:MM wall time or an absolute ISO timestamp. */
export function formatOpenHouseTime(
  value: string | null | undefined,
  eventTimeZone?: string | null,
): string | null {
  if (!value) return null;

  const clock = CLOCK_TIME_RE.exec(value);
  if (clock) {
    const hour = Number(clock[1]);
    const minute = Number(clock[2]);
    if (hour > 23 || minute > 59) return null;
    return formatClock(hour, minute);
  }

  const naive = NAIVE_LOCAL_RE.exec(value);
  if (naive) return formatClock(Number(naive[2]), Number(naive[3]));

  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolveOpenHouseTimeZone(eventTimeZone),
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '');
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return formatClock(hour % 24, minute);
}

/**
 * Wall-clock identity key for deduping the same open house across sources.
 * DB rows carry timestamptz instants (with offset) while remarks-synthesized
 * entries carry naive local ISO strings, so raw string equality never matches;
 * both are reduced to the local wall-clock label first.
 */
export function openHouseWallClockKey(
  date: string,
  startTime: string | null,
  timeZone?: string | null,
): string {
  if (!startTime) return `${date}|`;
  return `${date}|${formatOpenHouseTime(startTime, timeZone) ?? ''}`;
}

function formatClock(hour: number, minute: number): string {
  const period = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0
    ? `${hour12}${period}`
    : `${hour12}:${String(minute).padStart(2, '0')}${period}`;
}
